import { useForm } from "react-hook-form";
import { formatDate } from "@/lib/date-utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { UserStatus } from "@satyrsmc/shared/client";
import {
  ChangeEmailInputSchema,
  type ChangeEmailInput,
} from "@satyrsmc/shared/dto/member/settings";
import { trpc } from "@/trpc";
import { Button } from "@satyrsmc/shared/components";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";

const statusColors: Record<string, string> = {
  [UserStatus.Active]: "bg-green-500/10 text-green-700 border-green-500/30",
  [UserStatus.Locked]: "bg-purple-500/10 text-purple-700 border-purple-500/30",
  [UserStatus.Deactivated]: "bg-red-500/10 text-red-700 border-red-500/30",
};

export function AccountSettingsPage() {
  const { data, isLoading } = trpc.members.settings.getAccount.useQuery();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Account Settings</h1>

      {/* Account info */}
      {data && (
        <Card>
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs font-medium text-muted-foreground">Status</dt>
                <dd>
                  <Badge variant="outline" className={statusColors[data.user_status] ?? ""}>
                    {data.user_status}
                  </Badge>
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-muted-foreground">Type</dt>
                <dd className="text-sm">{data.user_type}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-muted-foreground">Email</dt>
                <dd className="text-sm">{data.email ?? "—"}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-muted-foreground">Joined</dt>
                <dd className="text-sm">{data.created_at ? formatDate(data.created_at) : "—"}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      )}

      <ChangeEmailForm />

      {/* Dues placeholder */}
      <Card>
        <CardHeader>
          <CardTitle>Dues & Payments</CardTitle>
          <CardDescription>Coming Soon</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Dues and payment management will be available in a future update.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function ChangeEmailForm() {
  const utils = trpc.useUtils();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ChangeEmailInput>({
    resolver: zodResolver(ChangeEmailInputSchema),
    defaultValues: { new_email: "" },
  });

  const mutation = trpc.members.settings.changeEmail.useMutation({
    onSuccess: () => {
      utils.members.settings.getAccount.invalidate();
      reset();
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Change Email</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={handleSubmit((data) => mutation.mutate(data))}
          className="space-y-4 max-w-md"
        >
          {mutation.error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {mutation.error.message}
            </div>
          )}
          {mutation.isSuccess && (
            <div className="rounded-md bg-green-500/10 p-3 text-sm text-green-700">
              Email changed successfully.
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="new_email">New Email</Label>
            <Input id="new_email" type="email" {...register("new_email")} />
            {errors.new_email && (
              <p className="text-sm text-destructive">{errors.new_email.message}</p>
            )}
          </div>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Changing..." : "Change Email"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
